import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, animate } from 'framer-motion';

const fontStack = "'Century Gothic','CenturyGothic','Tw Cen MT','Futura','Trebuchet MS','Nunito',sans-serif";

const brands = [
  { name: 'VICINITY STUDIO', accent: '#8AABB8', bg: '#4A5A67', desc: 'Video production that turns real stories into clear, compelling content.' },
  { name: 'VICINITY EVENTS', accent: '#E8B99A', bg: '#C37A53', desc: 'Event production, coverage, and AV support for experiences that run smoothly and land strongly.' },
  { name: 'VICINITY AGENCY', accent: '#9B72A3', bg: '#1A041D', desc: 'Social and brand content that helps ideas travel further and connect with the right audience.' },
  { name: 'VICINITY DESIGN', accent: '#5A9DC0', bg: '#194a68', desc: 'Visual and motion design that gives campaigns, content, and brands a sharper identity.' },
];

export default function AboutOverlay({ onClose }) {
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredBrand, setHoveredBrand] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => setScrolled(el.scrollTop > 60);
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToEcosystem = () => {
    const el = scrollRef.current;
    if (!el) return;
    animate(el.scrollTop, el.scrollHeight / 2, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: v => { el.scrollTop = v; },
    });
  };

  const scrollToTop = () => {
    const el = scrollRef.current;
    if (!el) return;
    animate(el.scrollTop, 0, {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: v => { el.scrollTop = v; },
    });
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ fontFamily: fontStack }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Background */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #0d0d0d 0%, #111318 50%, #0a0a0f 100%)' }} />
      <div className="absolute inset-0 pointer-events-none opacity-[0.035]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat', backgroundSize: '128px' }}
      />
      <div className="absolute top-[-10%] left-[-5%] w-[50vw] h-[50vw] max-w-[500px] max-h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(138,171,184,0.07) 0%, transparent 70%)', filter: 'blur(60px)' }} />
      <div className="absolute bottom-[-10%] right-[-5%] w-[45vw] h-[45vw] max-w-[450px] max-h-[450px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(155,114,163,0.07) 0%, transparent 70%)', filter: 'blur(60px)' }} />

      {/* Close */}
      <motion.button
        onClick={onClose}
        className="absolute top-6 right-6 md:top-8 md:right-8 z-20 flex items-center gap-2"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
      >
        <span style={{ fontFamily: fontStack, fontSize: 'clamp(8px,0.8vw,10px)', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>Close</span>
        <CloseX />
      </motion.button>

      {/* Scroll indicator — back to top when scrolled */}
      <AnimatePresence>
        {scrolled && (
          <motion.button
            key="back-top"
            onClick={scrollToTop}
            className="absolute top-6 left-6 md:top-8 md:left-8 z-20 flex items-center gap-2"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
              <path d="M13 5H1M1 5L5 1M1 5L5 9" stroke="rgba(255,255,255,0.4)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: fontStack, fontSize: 'clamp(8px,0.8vw,10px)', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)' }}>About</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Scrollable container */}
      <div
        ref={scrollRef}
        className="relative z-10 flex-1 overflow-y-auto"
        style={{ scrollSnapType: 'y mandatory', scrollBehavior: 'smooth' }}
      >
        {/* Page 1 — About */}
        <div
          className="flex flex-col items-center justify-center min-h-screen px-6 md:px-12"
          style={{ scrollSnapAlign: 'start' }}
        >
          <HeroSection onScrollDown={scrollToEcosystem} />
        </div>

        {/* Page 2 — Ecosystem */}
        <div
          className="flex flex-col items-center justify-center min-h-screen px-6 md:px-12 py-20"
          style={{ scrollSnapAlign: 'start' }}
        >
          <EcosystemSection hoveredBrand={hoveredBrand} setHoveredBrand={setHoveredBrand} />
        </div>
      </div>
    </motion.div>
  );
}

function CloseX() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <line x1="1" y1="1" x2="13" y2="13" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" />
      <line x1="13" y1="1" x2="1" y2="13" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

/* ─── Hero / About Section ───────────────────────────────────────────────── */
function HeroSection({ onScrollDown }) {
  const stagger = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };

  return (
    <motion.div
      className="flex flex-col items-center text-center max-w-2xl mx-auto"
      initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.h1
        {...stagger}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(2.2rem,6vw,5.5rem)', fontWeight: 300, letterSpacing: '-0.02em', color: '#ffffff', lineHeight: 1.08, marginBottom: 12 }}
      >
        Inspire Others
      </motion.h1>

      <motion.p
        {...stagger}
        transition={{ duration: 0.7, delay: 0.22, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(13px,1.4vw,18px)', fontWeight: 600, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.55)', marginBottom: 40, fontStyle: 'italic' }}
      >
        Through stories of others.
      </motion.p>

      <motion.div
        initial={{ scaleX: 0, opacity: 0 }}
        animate={{ scaleX: 1, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
        style={{ height: 1, width: 48, backgroundColor: 'rgba(255,255,255,0.15)', marginBottom: 40, transformOrigin: 'center' }}
      />

      <motion.p
        {...stagger}
        transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(12px,1.2vw,16px)', fontWeight: 400, letterSpacing: '0.04em', color: 'rgba(255,255,255,0.45)', lineHeight: 1.85, maxWidth: 560, marginBottom: 56 }}
      >
        Vicinity exists because we care about what surrounds us — our communities, our connections, and the relationships that shape how people experience the world.
      </motion.p>

      {/* Scroll down CTA */}
      <motion.button
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
        onClick={onScrollDown}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(8px,0.85vw,11px)', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 2, padding: '12px 32px', background: 'transparent', cursor: 'pointer', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', gap: 10, position: 'relative', overflow: 'hidden' }}
      >
        <motion.span className="absolute inset-0" initial={{ opacity: 0 }} whileHover={{ opacity: 1 }} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 2 }} />
        Explore our ecosystem
        <ScrollArrow />
      </motion.button>
    </motion.div>
  );
}

function ScrollArrow() {
  return (
    <motion.svg
      width="10" height="14" viewBox="0 0 10 14" fill="none"
      animate={{ y: [0, 3, 0] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      style={{ flexShrink: 0 }}
    >
      <path d="M5 1V13M5 13L1 9M5 13L9 9" stroke="rgba(255,255,255,0.6)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </motion.svg>
  );
}

/* ─── Ecosystem Section ──────────────────────────────────────────────────── */
function EcosystemSection({ hoveredBrand, setHoveredBrand }) {
  return (
    <motion.div
      className="flex flex-col items-center w-full max-w-4xl mx-auto"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.h2
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(1.4rem,3.5vw,3rem)', fontWeight: 300, letterSpacing: '-0.01em', color: '#ffffff', textAlign: 'center', marginBottom: 16, lineHeight: 1.15 }}
      >
        Distinct offerings.<br />One creative ecosystem.
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(11px,1.1vw,15px)', fontWeight: 500, letterSpacing: '0.06em', color: 'rgba(255,255,255,0.42)', textAlign: 'center', maxWidth: 520, lineHeight: 1.8, marginBottom: 48 }}
      >
        Across VICINITY, human stories become films, campaigns, events, and design systems that move people.
      </motion.p>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
        {brands.map((brand, i) => (
          <BrandCard
            key={brand.name}
            brand={brand}
            index={i}
            isHovered={hoveredBrand === i}
            isOther={hoveredBrand !== null && hoveredBrand !== i}
            onEnter={() => setHoveredBrand(i)}
            onLeave={() => setHoveredBrand(null)}
          />
        ))}
      </div>
    </motion.div>
  );
}

/* ─── Brand Card ──────────────────────────────────────────────────────────── */
function BrandCard({ brand, index, isHovered, isOther, onEnter, onLeave }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: isOther ? 0.35 : 1, y: 0 }}
      animate={{ opacity: isOther ? 0.35 : 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.15 + index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ position: 'relative', borderRadius: 4, padding: '28px', border: isHovered ? `1px solid ${brand.accent}44` : '1px solid rgba(255,255,255,0.07)', background: isHovered ? `linear-gradient(135deg, ${brand.bg}55 0%, rgba(0,0,0,0) 100%)` : 'rgba(255,255,255,0.025)', backdropFilter: 'blur(8px)', cursor: 'default', transition: 'border-color 0.4s ease, background 0.4s ease', overflow: 'hidden' }}
    >
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: `radial-gradient(ellipse at 0% 0%, ${brand.accent}12 0%, transparent 70%)`, borderRadius: 4 }}
      />
      <motion.p
        animate={{ color: isHovered ? brand.accent : 'rgba(255,255,255,0.55)' }}
        transition={{ duration: 0.35 }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(7px,0.75vw,9px)', fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', marginBottom: 12 }}
      >
        {brand.name}
      </motion.p>
      <motion.div
        animate={{ width: isHovered ? 32 : 16, backgroundColor: isHovered ? brand.accent : 'rgba(255,255,255,0.15)' }}
        transition={{ duration: 0.4 }}
        style={{ height: 1, marginBottom: 16 }}
      />
      <motion.p
        animate={{ color: isHovered ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.35)' }}
        transition={{ duration: 0.35 }}
        style={{ fontFamily: fontStack, fontSize: 'clamp(11px,1.05vw,14px)', fontWeight: isHovered ? 500 : 400, letterSpacing: '0.04em', lineHeight: 1.75 }}
      >
        {brand.desc}
      </motion.p>
    </motion.div>
  );
}